import {DOCUMENT} from '@angular/common';
import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  Inject,
  Injectable,
  Injector,
  NgZone, RendererFactory2,
  TemplateRef
} from '@angular/core';
import {Subject} from 'rxjs';

import {DbModalBackdropComponent} from './modal-backdrop';
import {DbModalOptions} from './modal-config';
import {ContentRef, DbActiveModal, DbModalRef} from './modal-ref';
import {DbModalWindowComponent} from './modal-window';

function isDefined(value: any): boolean {
  return value !== undefined && value !== null;
}

@Injectable({providedIn: 'root'})
export class DbModalStack {
  private _activeWindowCmptHasChanged = new Subject<void>();
  private _ariaHiddenValues: Map<Element, string | null> = new Map();
  private _backdropAttributes = ['animation', 'backdropClass'];
  private _modalRefs: DbModalRef[] = [];
  private _windowCmpts: ComponentRef<DbModalWindowComponent>[] = [];
  private _windowAttributes = ['animation', 'backdrop', 'position', 'height', 'width', 'hasCloseBtn', 'hasBorder', 'whiteCloseBtn', 'fullHeight', 'overflowHidden', 'opacity', 'backdropClass', 'hasOutsideCloseBtn', 'hasOutsideLeftCloseBtn', 'hasOutsideRightCloseBtn', 'hasCloseSharpBtn', 'fixMobile', 'isPaidFeature'];
  private _activeInstances: EventEmitter<DbModalRef[]> = new EventEmitter();

  constructor(
    private _applicationRef: ApplicationRef,
    private _injector: Injector,
    @Inject(DOCUMENT) private _document: Document,
    private _ngZone: NgZone,
    private _rendererFactory: RendererFactory2
  ) {
    this._activeWindowCmptHasChanged.subscribe(() => {
      if (this._windowCmpts.length) {
        const activeWindowCmpt = this._windowCmpts[this._windowCmpts.length - 1];
        this._revertAriaHidden();
        this._setAriaHidden(activeWindowCmpt.location.nativeElement);
      }
    });
  }

  open(moduleCFR: ComponentFactoryResolver, contentInjector: Injector,
       content: any, options: DbModalOptions): DbModalRef {
    const containerEl = this._document.body;
    const renderer = this._rendererFactory.createRenderer(null, null);
    const removeBodyClass = () => {
      if (!this._modalRefs.length) {
        renderer.removeClass(this._document.body, 'modal-open');
        this._revertAriaHidden();
      }
    };

    const activeModal = new DbActiveModal();
    const contentRef = this._getContentRef(moduleCFR, contentInjector, content, activeModal);

    const backdropCmptRef: ComponentRef<DbModalBackdropComponent> | undefined =
      options.backdrop !== false ? this._attachBackdrop(moduleCFR, containerEl) : undefined;
    const windowCmptRef: ComponentRef<DbModalWindowComponent> = this._attachWindowComponent(moduleCFR, containerEl, contentRef);
    const dbModalRef: DbModalRef = new DbModalRef(windowCmptRef, contentRef, backdropCmptRef, options.beforeDismiss);

    this._registerModalRef(dbModalRef);
    this._registerWindowCmpt(windowCmptRef);
    dbModalRef.result.then(removeBodyClass, removeBodyClass);
    activeModal.setBackdrop = (backdrop) => {
      dbModalRef.setBackdrop(backdrop);
    };
    activeModal.hideCloseBtn = (hide) => {
      dbModalRef.hideCloseBtn(hide);
    };
    activeModal.hideOutsideCloseBtn = (hide) => {
          dbModalRef.hideOutsideCloseBtn(hide);
    };
    activeModal.hasBorder = (hide) => {
      dbModalRef.hasBorder(hide);
    };
    activeModal.overflowHidden = (hide) => {
      dbModalRef.overflowHidden(hide);
    };
    activeModal.whiteCloseBtn = (white, opacity) => {
      dbModalRef.whiteCloseBtn(white, opacity);
    };
    activeModal.resize = (height, width) => {
      dbModalRef.resize(height, width);
    };
    activeModal.close = (result: any) => {
      result = dbModalRef.componentInstance?.modalClosedResult !== undefined && !result ? dbModalRef.componentInstance.modalClosedResult : result;
      dbModalRef.close(result);
    };
    activeModal.dismiss = (reason: any) => {
      reason = dbModalRef.componentInstance?.modalClosedResult !== undefined && !reason ? dbModalRef.componentInstance.modalClosedResult : reason;
      dbModalRef.dismiss(reason);
    };

    this._applyWindowOptions(windowCmptRef.instance, options);

    if (backdropCmptRef && backdropCmptRef.instance) {
      this._applyBackdropOptions(backdropCmptRef.instance, options);
      backdropCmptRef.changeDetectorRef.detectChanges();
    }
    windowCmptRef.changeDetectorRef.detectChanges();
    return dbModalRef;
  }

  get activeInstances() {
    return this._activeInstances;
  }

  dismissAll(reason?: any) {
    this._modalRefs.forEach(dbModalRef => dbModalRef.dismiss(reason));
  }

  hasOpenModals(): boolean {
    return this._modalRefs.length > 0;
  }

  private _attachBackdrop(
    moduleCFR: ComponentFactoryResolver, containerEl: any): ComponentRef<DbModalBackdropComponent> {
    const backdropFactory = moduleCFR.resolveComponentFactory(DbModalBackdropComponent);
    const backdropCmptRef = backdropFactory.create(this._injector);
    this._applicationRef.attachView(backdropCmptRef.hostView);
    containerEl.appendChild(backdropCmptRef.location.nativeElement);
    return backdropCmptRef;
  }

  private _attachWindowComponent(
    moduleCFR: ComponentFactoryResolver, containerEl: any, contentRef: any): ComponentRef<DbModalWindowComponent> {
    const windowFactory = moduleCFR.resolveComponentFactory(DbModalWindowComponent);
    const windowCmptRef = windowFactory.create(this._injector, contentRef.nodes);
    this._applicationRef.attachView(windowCmptRef.hostView);
    containerEl.appendChild(windowCmptRef.location.nativeElement);
    return windowCmptRef;
  }

  private _applyWindowOptions(windowInstance: DbModalWindowComponent, options: DbModalOptions): void {
    this._windowAttributes.forEach((optionName: string) => {
      if (isDefined(options[optionName])) {
        windowInstance[optionName] = options[optionName];
      }
    });
    
    // Explicitly set animation to 'scale' unless 'fade' is specified
    if (options.animation === 'fade') {
      windowInstance.animation = 'fade';
    } else {
      windowInstance.animation = 'scale'; // Default or explicit 'scale'
    }
  }

  private _applyBackdropOptions(backdropInstance: DbModalBackdropComponent, options: DbModalOptions): void {
    this._backdropAttributes.forEach((optionName: string) => {
      if (isDefined(options[optionName])) {
        backdropInstance[optionName] = options[optionName];
      }
    });
    
    // Ensure animation is properly set for backdrop
    backdropInstance.animation = true; // Always animate backdrop
  }

  private _getContentRef(
    moduleCFR: ComponentFactoryResolver, contentInjector: Injector,
    content: any, activeModal: DbActiveModal): ContentRef {
    if (!content) {
      return new ContentRef([]);
    } else if (content instanceof TemplateRef) {
      return this._createFromTemplateRef(content, activeModal);
    } else {
      return this._createFromComponent(moduleCFR, contentInjector, content, activeModal);
    }
  }

  private _createFromTemplateRef(content: TemplateRef<any>, activeModal: DbActiveModal): ContentRef {
    const context = {
      $implicit: activeModal,
      close(result) { activeModal.close(result); },
      dismiss(reason) { activeModal.dismiss(reason); }
    };
    const viewRef = content.createEmbeddedView(context);
    this._applicationRef.attachView(viewRef);
    return new ContentRef([viewRef.rootNodes], viewRef);
  }

  private _createFromComponent(
    moduleCFR: ComponentFactoryResolver, contentInjector: Injector,
    content: any, context: DbActiveModal): ContentRef {
    const contentCmptFactory = moduleCFR.resolveComponentFactory(content);
    const modalContentInjector =
      Injector.create({providers: [{provide: DbActiveModal, useValue: context}], parent: contentInjector});
    const componentRef = contentCmptFactory.create(modalContentInjector);
    const componentNativeEl = componentRef.location.nativeElement;
    this._applicationRef.attachView(componentRef.hostView);
    return new ContentRef([[componentNativeEl]], componentRef.hostView, componentRef);
  }

  private _setAriaHidden(element: Element) {
    const parent = element.parentElement;
    if (parent && element !== this._document.body) {
      Array.from(parent.children).forEach(sibling => {
        if (sibling !== element && sibling.nodeName !== 'SCRIPT') {
          this._ariaHiddenValues.set(sibling, sibling.getAttribute('aria-hidden'));
          sibling.setAttribute('aria-hidden', 'true');
        }
      });

      this._setAriaHidden(parent);
    }
  }

  private _revertAriaHidden() {
    this._ariaHiddenValues.forEach((value, element) => {
      if (value) {
        element.setAttribute('aria-hidden', value);
      } else {
        element.removeAttribute('aria-hidden');
      }
    });
    this._ariaHiddenValues.clear();
  }

  private _registerModalRef(dbModalRef: DbModalRef) {
    const unregisterModalRef = () => {
      const index = this._modalRefs.indexOf(dbModalRef);
      if (index > -1) {
        this._modalRefs.splice(index, 1);
        this._activeInstances.emit(this._modalRefs);
      }
    };
    this._modalRefs.push(dbModalRef);
    this._activeInstances.emit(this._modalRefs);
    dbModalRef.result.then(unregisterModalRef, unregisterModalRef);
  }

  private _registerWindowCmpt(ngbWindowCmpt: ComponentRef<DbModalWindowComponent>) {
    this._windowCmpts.push(ngbWindowCmpt);
    this._activeWindowCmptHasChanged.next();
    ngbWindowCmpt && ngbWindowCmpt.onDestroy(() => {
      const index = this._windowCmpts.indexOf(ngbWindowCmpt);
      if (index > -1) {
        this._windowCmpts.splice(index, 1);
        this._activeWindowCmptHasChanged.next();
      }
    });
  }
}
